import { createFileRoute } from '@tanstack/react-router'
import { useEffect, useMemo, useState } from 'react'
import { api } from '@/lib/api'
import { getAdminToken } from '@/lib/admin-store'
import {
  applyAdminFilters,
  getAdminNavLinks,
  readAdminFiltersFromSearch,
  type AdminFilters,
} from './-admin-utils'
import styles from './admin.module.css'

export const Route = createFileRoute('/admin/acquisition')({
  component: AdminAcquisitionRoute,
})

function toRangeDates(range: AdminFilters['range']): { from: string; to: string } {
  const to = new Date()
  const from = new Date()
  from.setUTCDate(from.getUTCDate() - (range === '30d' ? 30 : 7))
  return { from: from.toISOString(), to: to.toISOString() }
}

function AdminAcquisitionRoute() {
  const [token] = useState(getAdminToken)
  const [filters, setFilters] = useState<AdminFilters>(readAdminFiltersFromSearch)
  const [funnel, setFunnel] = useState<Awaited<
    ReturnType<typeof api.adminAcquisitionFunnel>
  > | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)

  const navLinks = useMemo(() => getAdminNavLinks(filters), [filters])
  const noData = (funnel?.stages ?? []).every((stage) => stage.count === 0)

  useEffect(() => {
    const load = async (): Promise<void> => {
      if (!token) {
        return
      }

      setIsLoading(true)
      setError(null)

      try {
        const range = toRangeDates(filters.range)
        const data = await api.adminAcquisitionFunnel(token, {
          from: range.from,
          to: range.to,
          granularity: 'day',
        })
        setFunnel(data)
      } catch (loadError) {
        setError(
          loadError instanceof Error ? loadError.message : 'Failed to load acquisition funnel.'
        )
      } finally {
        setIsLoading(false)
      }
    }

    void load()
  }, [filters.range, token])

  return (
    <section className={styles.layout}>
      <header className={styles.hero}>
        <h1 className={styles.title}>Acquisition Funnel</h1>
        <p className={styles.subtitle}>
          Funnel stages from landing through paid conversion, with drill-down routing.
        </p>
      </header>

      <section className={styles.card}>
        <div className={styles.tabs}>
          {navLinks.map((link) => (
            <a key={link.path} href={link.href} className={styles.tabLink}>
              {link.label}
            </a>
          ))}
        </div>
        <div className={styles.row}>
          <label>
            Range
            <select
              className={styles.select}
              value={filters.range}
              onChange={(event) =>
                setFilters((current) => ({
                  ...current,
                  range: event.target.value === '30d' ? '30d' : '7d',
                }))
              }
            >
              <option value="7d">Last 7 Days</option>
              <option value="30d">Last 30 Days</option>
            </select>
          </label>
          <label>
            Source
            <input
              className={styles.input}
              value={filters.source}
              onChange={(event) =>
                setFilters((current) => ({ ...current, source: event.target.value }))
              }
              placeholder="promoter, direct, organic"
            />
          </label>
          <label>
            Country
            <input
              className={styles.input}
              value={filters.country}
              onChange={(event) =>
                setFilters((current) => ({ ...current, country: event.target.value }))
              }
              placeholder="US"
            />
          </label>
          <button
            className={styles.secondaryButton}
            type="button"
            onClick={() => applyAdminFilters(filters)}
          >
            Apply Filters
          </button>
          <button
            className={styles.ghostButton}
            type="button"
            onClick={() =>
              applyAdminFilters({
                range: '7d',
                source: '',
                country: '',
                status: 'all',
                query: '',
              })
            }
          >
            Clear Filters
          </button>
        </div>
      </section>

      {!token && (
        <section className={styles.card}>
          <p className={styles.emptyState}>You don't have admin access.</p>
          <a className={styles.tabLink} href="/admin">
            Go to Home
          </a>
        </section>
      )}

      {token && isLoading && (
        <section className={styles.card}>
          <p className={styles.muted}>Loading acquisition funnel...</p>
        </section>
      )}

      {token && error && (
        <section className={styles.card}>
          <p className={styles.error}>{error}</p>
          <button
            className={styles.secondaryButton}
            type="button"
            onClick={() =>
              applyAdminFilters({
                ...filters,
              })
            }
          >
            Retry
          </button>
        </section>
      )}

      {token && funnel && !error && (
        <section className={styles.card}>
          <h2>Stage Counts</h2>
          {noData ? (
            <>
              <p className={styles.emptyState}>No attributed traffic found.</p>
              <button
                className={styles.secondaryButton}
                type="button"
                onClick={() =>
                  applyAdminFilters({
                    range: filters.range,
                    source: '',
                    country: '',
                    status: 'all',
                    query: '',
                  })
                }
              >
                Clear Filters
              </button>
            </>
          ) : (
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Stage</th>
                  <th>Count</th>
                </tr>
              </thead>
              <tbody>
                {funnel.stages.map((stage) => (
                  <tr key={stage.name}>
                    <td>{stage.name}</td>
                    <td>{stage.count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>
      )}
    </section>
  )
}
